import { toText } from './text';

/*
 * The Magic Search: one typed text held against a whole record rather
 * than against one field of it — which is why a fragment of a uuid, a
 * phone number or a postal code finds its entry even where no column
 * and no line of the list shows it.
 *
 * The tables search this way (see `CrudTable`), and so do the
 * searchable dropdowns. Keeping the rule in one place is what lets
 * "magic" mean the same thing in both.
 */

/* what counts as searchable at all: text and numbers. Booleans stay
   out — nobody searches for "true" — and so do the property names:
   they are code, not data */
export function isText(value: unknown): value is string | number {
  return typeof value === 'string' || typeof value === 'number';
}

/*
 * Every string and number anywhere in the value — the raw material of
 * the haystack. `out` is filled rather than returned so that a caller
 * can gather several records into one list.
 */
export function collectMagicLeaves(
  value: unknown,
  out: string[] = []
): string[] {
  if (value == null) return out;
  if (typeof value === 'object') {
    Object.values(value).forEach((leaf) => collectMagicLeaves(leaf, out));
    return out;
  }
  if (isText(value)) out.push(String(value));
  return out;
}

/* what a typed query means: no case, no surrounding blanks */
export function magicNeedle(text: unknown): string {
  return toText(text ?? '')
    .trim()
    .toLowerCase();
}

/*
 * The first leaf of the record carrying the needle — the evidence for
 * a match. A table row can show what it was found by in one of its
 * columns; a one-line list entry cannot, so it says it instead.
 *
 * Returns null where nothing matches, which is also the answer for a
 * record nobody handed in: a caller without one gets no deep search,
 * only the plain one over what it displays.
 */
export function findMagicLeaf(record: unknown, needle: string): string | null {
  if (!needle || record == null) return null;
  return (
    collectMagicLeaves(record).find((leaf) =>
      leaf.toLowerCase().includes(needle)
    ) ?? null
  );
}
